import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";

import { AppModel, ContractTypeAwsPpa, type ContractTypeOption } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { useDocumentDataOnce } from "@doitintl/models-hooks";
import orderBy from "lodash/orderBy";

export type ContractTypesContext = {
  contractProductTypes: ContractTypeOption[];
  contractProductSubtypes: ContractTypeOption[];
  loading: boolean;
  getContractTypeLabel: (value: string) => string;
  getContractTypeLabelWithSubtype: (type: string, subtype?: string) => string;
};

const ContractTypesContext = createContext<ContractTypesContext>({
  contractProductTypes: [],
  contractProductSubtypes: [],
  loading: false,
  getContractTypeLabel: () => "",
  getContractTypeLabelWithSubtype: () => "",
});

type ContractTypesContextProviderProps = {
  children: ReactNode;
};

export const ContractTypesContextProvider = ({ children }: ContractTypesContextProviderProps) => {
  const [contractsMetadata, loading] = useDocumentDataOnce(getCollection(AppModel).doc("contracts"));
  const contractProductTypes = useMemo(
    () => orderBy(contractsMetadata?.options ?? [], (o) => o.label),
    [contractsMetadata?.options]
  );
  const contractProductSubtypes = useMemo(
    () => orderBy(contractsMetadata?.awsPpaOptions ?? [], (o) => o.label),
    [contractsMetadata?.awsPpaOptions]
  );

  const getContractTypeLabel = useCallback(
    (value: string) => {
      const contractType = contractProductTypes.find((type) => type.value === value);
      return contractType?.label ?? "";
    },
    [contractProductTypes]
  );

  const getContractTypeLabelWithSubtype = useCallback(
    (type: string, subtype?: string) => {
      const contractType = contractProductTypes.find((t) => t.value === type);
      let label = contractType?.label ?? "";
      if (type === ContractTypeAwsPpa && subtype) {
        const contractSubtype = contractProductSubtypes.find((s) => s.value === subtype);
        label = `${label} ${contractSubtype?.label ?? ""}`.trim();
      }
      return label;
    },
    [contractProductSubtypes, contractProductTypes]
  );

  const value = useMemo(
    () => ({
      contractProductTypes,
      contractProductSubtypes,
      loading,
      getContractTypeLabel,
      getContractTypeLabelWithSubtype,
    }),
    [contractProductTypes, contractProductSubtypes, loading, getContractTypeLabel, getContractTypeLabelWithSubtype]
  );

  return <ContractTypesContext.Provider value={value}>{children}</ContractTypesContext.Provider>;
};

export const useContractTypesContext = () => useContext(ContractTypesContext);

type ContractTypesContextProviderForTestingProps = {
  children?: ReactNode;
  value?: Partial<ContractTypesContext>;
};

export const ContractTypesContextProviderForTesting = ({
  children,
  value,
}: ContractTypesContextProviderForTestingProps) => {
  const contractProductTypes = useMemo(() => value?.contractProductTypes ?? [], [value?.contractProductTypes]);
  const contractProductSubtypes = useMemo(() => value?.contractProductSubtypes ?? [], [value?.contractProductSubtypes]);

  const getContractTypeLabel = useCallback(
    (val: string) => {
      const contractType = contractProductTypes.find((type) => type.value === val);
      return contractType?.label ?? "";
    },
    [contractProductTypes]
  );

  const getContractTypeLabelWithSubtype = useCallback(
    (type: string, subtype?: string) => {
      const contractType = contractProductTypes.find((t) => t.value === type);
      let label = contractType?.label ?? "";
      if (type === ContractTypeAwsPpa && subtype) {
        const contractSubtype = contractProductSubtypes.find((s) => s.value === subtype);
        label = `${label} ${contractSubtype?.label ?? ""}`.trim();
      }
      return label;
    },
    [contractProductSubtypes, contractProductTypes]
  );

  const actualValue = useMemo(
    () => ({
      contractProductTypes,
      contractProductSubtypes,
      loading: value?.loading ?? false,
      getContractTypeLabel,
      getContractTypeLabelWithSubtype,
    }),
    [
      contractProductSubtypes,
      contractProductTypes,
      getContractTypeLabel,
      getContractTypeLabelWithSubtype,
      value?.loading,
    ]
  );

  return <ContractTypesContext.Provider value={actualValue}>{children}</ContractTypesContext.Provider>;
};
